import { Slider as RSlider, Switch as RSwitch, ToggleGroup } from 'radix-ui';
import { forwardRef, useId, type InputHTMLAttributes, type ReactNode, type TextareaHTMLAttributes } from 'react';
import { cn } from '@/lib/cn';

const fieldBase =
  'w-full rounded-md border border-line bg-mat px-2.5 text-[0.95rem] text-ink placeholder:text-ink-3 transition-colors hover:border-line-strong focus-visible:border-focus focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus/30';

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(function Input(
  { className, ...props },
  ref,
) {
  return <input ref={ref} className={cn(fieldBase, 'h-9 tabular-nums', className)} {...props} />;
});

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(function Textarea(
  { className, ...props },
  ref,
) {
  return <textarea ref={ref} className={cn(fieldBase, 'min-h-20 resize-y py-2 leading-snug', className)} {...props} />;
});

/** Etiqueta + control. El id se genera solo y se pasa al control con render prop. */
export function Field({
  label,
  hint,
  children,
  className,
}: {
  label: ReactNode;
  hint?: ReactNode;
  children: (id: string) => ReactNode;
  className?: string;
}) {
  const id = useId();
  return (
    <div className={cn('flex min-w-0 flex-col gap-1', className)}>
      <label htmlFor={id} className="label-caps">
        {label}
      </label>
      {children(id)}
      {hint && <p className="text-xs leading-snug text-ink-2">{hint}</p>}
    </div>
  );
}

export function Switch({
  checked,
  onCheckedChange,
  label,
  id,
}: {
  checked: boolean;
  onCheckedChange: (v: boolean) => void;
  label: ReactNode;
  id?: string;
}) {
  const autoId = useId();
  const switchId = id ?? autoId;
  return (
    <div className="flex items-center justify-between gap-3">
      <label htmlFor={switchId} className="text-[0.92rem] leading-snug text-ink">
        {label}
      </label>
      <RSwitch.Root
        id={switchId}
        checked={checked}
        onCheckedChange={onCheckedChange}
        className="relative h-5.5 w-9.5 shrink-0 rounded-full border border-line-strong bg-surface-3 transition-colors data-[state=checked]:border-accent data-[state=checked]:bg-accent"
      >
        <RSwitch.Thumb className="block h-4 w-4 translate-x-0.5 rounded-full bg-surface shadow transition-transform data-[state=checked]:translate-x-4.5" />
      </RSwitch.Root>
    </div>
  );
}

export function Slider({
  value,
  onValueChange,
  onValueCommit,
  min,
  max,
  step = 1,
  label,
  id,
}: {
  value: number;
  onValueChange: (v: number) => void;
  onValueCommit?: (v: number) => void;
  min: number;
  max: number;
  step?: number;
  label: string;
  id?: string;
}) {
  return (
    <RSlider.Root
      id={id}
      className="relative flex h-6 w-full touch-none items-center select-none"
      value={[value]}
      min={min}
      max={max}
      step={step}
      onValueChange={([v]) => v !== undefined && onValueChange(v)}
      onValueCommit={([v]) => v !== undefined && onValueCommit?.(v)}
    >
      <RSlider.Track className="relative h-1.5 grow overflow-hidden rounded-full bg-surface-3">
        <RSlider.Range className="absolute h-full bg-accent" />
      </RSlider.Track>
      <RSlider.Thumb
        aria-label={label}
        className="block h-4.5 w-4.5 rounded-full border-2 border-accent bg-surface shadow transition-transform hover:scale-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus/40"
      />
    </RSlider.Root>
  );
}

export function Segmented<T extends string>({
  value,
  onValueChange,
  options,
  label,
  size = 'md',
}: {
  value: T;
  onValueChange: (v: T) => void;
  options: readonly { value: T; label: ReactNode; title?: string }[];
  label: string;
  size?: 'sm' | 'md';
}) {
  return (
    <ToggleGroup.Root
      type="single"
      value={value}
      onValueChange={(v) => {
        const found = options.find((o) => o.value === v);
        if (found) onValueChange(found.value);
      }}
      aria-label={label}
      className="grid auto-cols-fr grid-flow-col gap-0.5 rounded-md border border-line bg-surface-2 p-0.5"
    >
      {options.map((o) => (
        <ToggleGroup.Item
          key={o.value}
          value={o.value}
          title={o.title}
          aria-label={o.title}
          className={cn(
            'inline-flex items-center justify-center gap-1 rounded-[5px] px-1.5 font-medium text-ink-2 transition-colors hover:text-ink data-[state=on]:bg-surface data-[state=on]:text-ink data-[state=on]:shadow-sm',
            size === 'sm' ? 'h-7 text-[0.82rem]' : 'h-8 text-[0.9rem]',
          )}
        >
          {o.label}
        </ToggleGroup.Item>
      ))}
    </ToggleGroup.Root>
  );
}

export function Swatches({
  colors,
  value,
  onChange,
  label,
}: {
  colors: readonly string[];
  value: string;
  onChange: (c: string) => void;
  label: string;
}) {
  return (
    <div role="radiogroup" aria-label={label} className="flex flex-wrap gap-1.5">
      {colors.map((c) => (
        <button
          key={c}
          type="button"
          role="radio"
          aria-checked={c.toLowerCase() === value.toLowerCase()}
          aria-label={c}
          onClick={() => onChange(c)}
          className="h-6.5 w-6.5 rounded-full border-2 border-surface shadow-[0_0_0_1px_var(--c-line-strong)] transition-transform hover:scale-110 aria-checked:shadow-[0_0_0_2px_var(--c-ink)]"
          style={{ background: c }}
        />
      ))}
    </div>
  );
}

/** Número entero con botones − / +. */
export function NumberField({
  value,
  onChange,
  min,
  max,
  id,
  suffix,
  step = 1,
}: {
  value: number;
  onChange: (v: number) => void;
  min: number;
  max: number;
  id?: string;
  suffix?: ReactNode;
  step?: number;
}) {
  const set = (v: number) => onChange(Math.min(max, Math.max(min, Math.round(v))));
  return (
    <div className="flex items-center gap-1">
      <button
        type="button"
        className="h-9 w-8 rounded-md border border-line bg-surface-2 text-lg leading-none text-ink-2 hover:text-ink disabled:opacity-40"
        onClick={() => set(value - step)}
        disabled={value <= min}
        aria-label="−"
      >
        −
      </button>
      <Input
        id={id}
        type="number"
        inputMode="numeric"
        min={min}
        max={max}
        value={value}
        onChange={(e) => {
          const v = Number.parseInt(e.target.value, 10);
          if (!Number.isNaN(v)) set(v);
        }}
        className="w-16 text-center"
      />
      <button
        type="button"
        className="h-9 w-8 rounded-md border border-line bg-surface-2 text-lg leading-none text-ink-2 hover:text-ink disabled:opacity-40"
        onClick={() => set(value + step)}
        disabled={value >= max}
        aria-label="+"
      >
        +
      </button>
      {suffix && <span className="ml-1 text-sm whitespace-nowrap text-ink-2 tabular-nums">{suffix}</span>}
    </div>
  );
}
